import { styled } from '@mui/material/styles';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { ClientAdmin } from '../../../data/models/client.model';
import { useIsSuperReaderUser } from '../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'Admin';

const classes = {
  title: `${PREFIX}-title`,
  resendLink: `${PREFIX}-resendLink`,
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`& .${classes.title}`]: {
    fontWeight: 700,
  },

  [`& .${classes.resendLink}`]: {
    fontSize: 14,
    fontWeight: '700',
    textDecoration: 'none',
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
}));

interface AdminProps {
  clientAdmin: ClientAdmin;
  deleteAdmin: (admin: ClientAdmin) => void;
  resendInvite: (admin: ClientAdmin) => void;
  readonly: boolean;
}

export default function Admin({
  clientAdmin,
  deleteAdmin,
  resendInvite,
  readonly,
}: AdminProps) {
  const t = useCommonTranslation();
  const isSuperReaderUser = useIsSuperReaderUser();

  const handleResendInvite = () => {
    resendInvite(clientAdmin);
  };

  const isAdminPending = !clientAdmin.isEmailVerified;

  return (
    <StyledCard elevation={2}>
      <CardContent>
        <Box className={classes.title}>
          {clientAdmin.userFirstName} {clientAdmin.userLastName}
        </Box>
        <Box>
          {clientAdmin.emailAddress}{' '}
          {isAdminPending ? `(${t('client.pending')})` : ''}
        </Box>
        {isAdminPending && !isSuperReaderUser && (
          <Box>
            <Typography
              component={Link}
              className={classes.resendLink}
              onClick={handleResendInvite}>
              {t('client.resendInvite')}
            </Typography>
          </Box>
        )}
      </CardContent>
      {!readonly && !isSuperReaderUser && (
        <CardActions>
          <Tooltip title={t('client.deleteAdmin')}>
            <span>
              <IconButton
                data-testid="delete-admin"
                onClick={(evt) => deleteAdmin(clientAdmin)}
                color="error"
                size="large">
                <DeleteForeverIcon />
              </IconButton>
            </span>
          </Tooltip>
        </CardActions>
      )}
    </StyledCard>
  );
}
